import React, { useMemo } from 'react';
import {
  DEFAULT_TRIGGER_TIME,
  TYPE_ADDRESS,
  TYPE_CONFIRM_PASSWORD,
  TYPE_EMAIL,
  TYPE_FIRST_NAME,
  TYPE_LAST_NAME,
  TYPE_NICKNAME,
  TYPE_NIN,
  TYPE_OTP,
  TYPE_PASSWORD,
  TYPE_PHONE,
  TYPE_USERNAME,
} from '@/components/modules/register/register-dynamic/const';
import NINValidator from '@/modules/casino/utils/NINValidator';
import PhoneNumberValidator from '@/modules/casino/utils/PhoneNumberValidator';
import PasswordValidator from '@/utils/PasswordValidator';
import { useTranslation } from 'react-i18next';
import { RegisterModuleStateProps } from '@/components/modules/register/register-dynamic/types';
import * as EmailValidator from 'email-validator';
import ExpiringLocalStorage from '@/modules/casino/utils/ExpiringLocalStorage';
import { useAppDispatch, useAppSelector } from '@/store';
import ResponseErrorMessages from '@/modules/casino/store/errors/ResponseErrorMessages';
import { useRegisterConfig } from '@/components/modules/register/register-dynamic/hooks/useRegisterConfig';
import {
  loginMethod,
  loginPassword,
  loginStart,
  loginUsername,
  logout,
  validateField,
} from '@/modules/casino/store/actions';
import { useNavigate } from 'react-router-dom';
import { setCanNavigate as setCanNavigateAction } from '@/modules/casino/store/actions/navigation';
import { IS_PHONE } from '@/modules/casino/utils/LoginMethods';

export const OTP_REGISTER_EASY = 1;
export const OTP_REGISTER_HARD = 2;
export const OTP_REGISTER_AUTO_LOGIN = 3;

export function useRegisterForm(
  state: RegisterModuleStateProps,
  setState: React.Dispatch<React.SetStateAction<RegisterModuleStateProps>>,
  properties: any,
) {
  const { errors, partialAccount, SMSType } = useAppSelector<any>((state) => state.register);
  const { phoneValidationMessage } = useAppSelector<any>((state) => state.allCountries);
  const { signedUp } = useAppSelector<any>((state) => state.register);
  const { client_player_id } = useAppSelector<any>((state) => state.profile);
  const dispatch = useAppDispatch();
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const druid: { inProgress: boolean; data: { [key: string]: any } } = useAppSelector<any>((state) => state.druid);
  const { currentCountry } = useAppSelector<any>((state) => state.allCountries);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { loginError, inRequest } = useAppSelector((state) => state.login);

  const [OTPType, setOTPType] = React.useState<number>(OTP_REGISTER_EASY);

  const config = useRegisterConfig(state, setState, properties?.config);

  const loadRegisterFieldsFromStorage = React.useCallback(() => {
    const loadedFields = ExpiringLocalStorage.get('registerDynamicFields');

    if (loadedFields) {
      return JSON.parse(loadedFields);
    }
    return {};
  }, []);

  const getValuesFromLocalStorage = React.useCallback(() => {
    const loadedFields = loadRegisterFieldsFromStorage();
    const fields = { ...state.fields };
    const checkboxes = { ...state.checkboxes };
    const needsValidation: { type: string; value: string }[] = [];
    if (loadedFields) {
      Object.keys(loadedFields).forEach((key) => {
        if (loadedFields[key].type === 'field') {
          // @ts-ignore
          if (!fields[key]) {
            // @ts-ignore
            fields[key] = {};
          }
          // @ts-ignore
          fields[key].value = loadedFields[key].val;
          // @ts-ignore
          needsValidation.push({ type: key, value: loadedFields[key].val });
        } else if (loadedFields[key].type === 'checkbox') {
          // @ts-ignore
          if (!checkboxes[key]) {
            // @ts-ignore
            checkboxes[key] = {};
          }
          // @ts-ignore
          checkboxes[key].checked = loadedFields[key].val;
        }
      });
    }

    return { fields, checkboxes, needsValidation };
  }, [loadRegisterFieldsFromStorage, state.fields, state.checkboxes]);

  const validatePhoneField = React.useCallback(
    (value: string, validator: any) => {
      validator.validate(value, currentCountry).then((valid: boolean) => {
        if (!valid) {
          setState((prevState: RegisterModuleStateProps) => ({
            ...prevState,
            fields: {
              ...prevState.fields,
              phone: {
                ...prevState.fields.phone,
                error: 'REGISTER.INPUT.ERROR.PHONE-INVALID',
              },
            },
            trigger: 'fieldValidator-invalid',
          }));
        } else {
          // this will trigger server validation if we have usernameServerValidation (e.g.) set to true as internal attributes on register module template;
          if (properties['phoneServerValidation']) {
            dispatch(validateField(value, 'field'));
          } else {
            setState((prevState: RegisterModuleStateProps) => ({
              ...prevState,
              fields: {
                ...prevState.fields,
                phone: {
                  ...prevState.fields.phone,
                  error: undefined,
                },
              },
              trigger: 'phoneValidator-valid',
            }));
          }
        }
      });
    },
    [currentCountry, dispatch, properties, setState],
  );

  const validateFieldLocal = React.useCallback(
    (field: string, value: string, validator: any) => {
      if (value === '') {
        setState((prevState: RegisterModuleStateProps) => ({
          ...prevState,
          fields: {
            ...prevState.fields,
            [field]: {
              ...prevState.fields[field],
              error: undefined,
            },
          },
          trigger: 'FieldValidator-empty',
        }));
        return;
      }

      if (field === 'phone') {
        validatePhoneField(value, validator);
      } else {
        const valid = validator.validate(value);

        if (!valid) {
          setState((prevState: RegisterModuleStateProps) => ({
            ...prevState,
            fields: {
              ...prevState.fields,
              [field]: {
                ...prevState.fields[field],
                error: `REGISTER.INPUT.ERROR.${field.toUpperCase()}-INVALID`,
              },
            },
            trigger: 'fieldValidator-invalid',
          }));
        } else {
          // this will trigger server validation if we have usernameServerValidation (e.g.) set to true as internal attributes on register module template;
          if (properties[`${field}ServerValidation`]) {
            if (field === 'username') {
              dispatch(
                validateField(value, field, {
                  taxId: state.fields.nin.value,
                }),
              );
            } else {
              dispatch(validateField(value, field));
            }
          } else {
            setState((prevState: RegisterModuleStateProps) => ({
              ...prevState,
              fields: {
                ...prevState.fields,
                [field]: {
                  ...prevState.fields[field],
                  error: undefined,
                },
              },
              trigger: `fieldValidator-valid-${field}`,
            }));
          }
        }
      }

      /*
    const valid = field === 'phone' ? validator.validate(value, currentCountry) : validator.validate(value);

    // console.log('VALIDATE FIELD LOCAL valid', { field, value, validator, valid });
    if (!valid) {
      setState((prevState: RegisterModuleStateProps) => ({
        ...prevState,
        fields: {
          ...prevState.fields,
          [field]: {
            ...prevState.fields[field],
            error: `REGISTER.INPUT.ERROR.${field.toUpperCase()}-INVALID`,
          },
        },
        trigger: 'fieldValidator-invalid',
      }));
    } else {
      // this will trigger server validation if we have usernameServerValidation (eg.) set to true as internal attributes on register module template;
      if (properties[`${field}ServerValidation`]) {
        dispatch(validateField(value, field));
      } else {
        setState((prevState: RegisterModuleStateProps) => ({
          ...prevState,
          fields: {
            ...prevState.fields,
            [field]: {
              ...prevState.fields[field],
              error: undefined,
            },
          },
          trigger: 'emailValidator-valid',
        }));
      }
    }
  */
    },
    [dispatch, properties, setState, state.fields.nin?.value, validatePhoneField],
  );

  const validateUsernameField = React.useCallback((value: string, validator: any) => {}, []);

  const passwordValidator = React.useCallback(
    (password: string) => {
      const passwordIsValid = PasswordValidator(password, t);

      setState((prevState: RegisterModuleStateProps) => ({
        ...prevState,
        passwordStrengthScore: passwordIsValid.score,
        passwordStrengthShortLabel: passwordIsValid.labels.short,
        passwordStrengthLongLabel: passwordIsValid.labels.long,
        passwordConditionsNotMet: passwordIsValid.conditionsNotMet,
        passwordMessagePlaceholders: passwordIsValid.placeholders,
        fields: {
          ...prevState.fields,
          password: {
            ...prevState.fields.password,
            error: passwordIsValid.success ? false : passwordIsValid.message,
          },
        },
        trigger: 'passwordValidator',
      }));
    },
    [setState, t],
  );

  const triggerValidator = React.useCallback(
    (type: string, value: string, time: number) => {
      // delayed validation to prevent multiple calls
      if (timer) {
        clearTimeout(timer);
      }

      const lTimer = setTimeout(() => {
        switch (type) {
          case TYPE_EMAIL:
            validateFieldLocal(type, value, EmailValidator);
            break;
          case TYPE_PHONE:
            validateFieldLocal(type, value, PhoneNumberValidator);
            break;
          case TYPE_PASSWORD:
            passwordValidator(value);
            break;
          case TYPE_CONFIRM_PASSWORD:
            validateFieldLocal(type, value, { validate: (value: string) => value === state.fields.password.value }); // TODO: Move validator to useCallback
            break;
          case TYPE_NICKNAME:
            validateFieldLocal(type, value, { validate: (value: string) => value.length >= 4 && value.length <= 100 });
            break;
          case TYPE_FIRST_NAME:
          case TYPE_LAST_NAME:
            validateFieldLocal(type, value, { validate: (value: string) => value.length >= 3 && value.length <= 100 });
            break;
          case TYPE_NIN:
            validateFieldLocal(type, value, NINValidator);
            break;
          case TYPE_OTP:
            validateFieldLocal(type, value, { validate: (value: string) => value.length > 3 });
            break;
          case TYPE_USERNAME:
            validateFieldLocal(type, value, {
              validate: (value: string) => value.length >= 5 && value.length <= 12 && /^[a-zA-Z0-9]+$/.test(value),
            });
            break;
          default:
            break;
        }
      }, time);
      setTimer(lTimer);
    },
    [passwordValidator, state.fields.password?.value, timer, validateFieldLocal],
  );

  const loadFieldsFromLocalStorage = React.useCallback(() => {
    const { fields, checkboxes, needsValidation } = getValuesFromLocalStorage();

    setState((prevState) => {
      return {
        ...prevState,
        // ...initialState,
        fields: fields,
        checkboxes: checkboxes,
        trigger: 'useEffect-[]',
      };
    });

    needsValidation.forEach((field) => {
      triggerValidator(field.type, field.value, 0);
    });
  }, [getValuesFromLocalStorage, setState, triggerValidator]);

  React.useEffect(() => {
    if (SMSType) {
      setOTPType(SMSType);
    }
  }, [SMSType]);

  React.useEffect(() => {
    // loadFieldsFromLocalStorage(); TODO: uncomment this line and fix the issues
  }, []);

  React.useEffect(() => {
    const errorMessage = 'REGISTER.INPUT.ERROR.OCR-FAILED';
    if (!druid.inProgress && druid.data.ResultId) {
      const globalConfidence = 0.6;

      let readingOK = druid.data.Status === 'Complete';
      if (readingOK) {
        // check confidence for each field
        readingOK =
          druid.data.Confidence >= globalConfidence &&
          druid.data.Data['Face.Confidence'] >= globalConfidence &&
          druid.data.Data['Nume.Confidence'] >= globalConfidence &&
          druid.data.Data['Prenume.Confidence'] >= globalConfidence &&
          druid.data.Data['CNP.Confidence'] >= globalConfidence &&
          druid.data.Data['Domiciliu.Confidence'] >= globalConfidence;

        if (readingOK) {
          setState((prevState) => ({
            ...prevState,
            fields: {
              ...prevState.fields,
              firstName: {
                ...prevState.fields.firstName,
                value: druid.data.Data.Prenume,
              },
              lastName: {
                ...prevState.fields.lastName,
                value: druid.data.Data.Nume,
              },
              nin: {
                ...prevState.fields.nin,
                value: druid.data.Data.CNP,
              },
              address: {
                ...prevState.fields.address,
                value: druid.data.Data.Domiciliu,
              },
            },
            loadingDocumentUpload: false,
            currentStep: config.stepsMap.name > 0 ? config.stepsMap.name : prevState.currentStep,
            trigger: 'druid',
          }));

          triggerValidator(TYPE_FIRST_NAME, druid.data.Data.Prenume, 0);
          triggerValidator(TYPE_LAST_NAME, druid.data.Data.Nume, 0);
          triggerValidator(TYPE_NIN, druid.data.Data.CNP, 0);
          triggerValidator(TYPE_ADDRESS, druid.data.Data.Domiciliu, 0);
        }
      }

      setState((prevState) => ({
        ...prevState,
        fields: {
          ...prevState.fields,
          ocr_scan: {
            ...prevState.fields.ocr_scan,
            value: readingOK ? druid.data.ResultId : null,
            error: readingOK ? undefined : errorMessage,
          },
        },
        OCRModalOpen: false,
        loadingDocumentUpload: false,
        trigger: 'druid-failed',
      }));
    } else {
      if (druid.data.error) {
        setState((prevState) => ({
          ...prevState,
          fields: {
            ...prevState.fields,
            ocr_scan: {
              ...prevState.fields.ocr_scan,
              error: errorMessage,
            },
          },
          OCRModalOpen: false,
          loadingDocumentUpload: false,
          trigger: 'druid-failed-2',
        }));
      }
    }
  }, [druid]);

  React.useEffect(() => {
    // loop through errors and set stateconsole.log('useRegisterForm useEffect-errors', { errors, phoneValidationMessage, state });
    const fields = {
      ...state.fields,
    };
    Object.keys(errors).forEach((key) => {
      let error: boolean | string | undefined = undefined;
      if (errors[key]) {
        const lError = errors[key];
        if (typeof lError.ResponseCode !== 'undefined') {
          error = ResponseErrorMessages.get(lError.ResponseCode);
        } else {
          if (typeof lError === 'string') {
            error = lError;
          }
        }

        // if (key === 'phone') {
        //   error = phoneValidationMessage;
        // }
      } else {
        error = false;
      }
      // @ts-ignore
      if (!fields[key]) {
        // @ts-ignore
        fields[key] = {};
      }

      if (typeof error !== 'undefined') {
        // @ts-ignore
        fields[key].error = error;
      } else if (errors?.[key]?.length === 0) {
        // @ts-ignore
        fields[key].error = false;
      }
    });

    let errorStep: number | undefined = state.currentStep;
    const regErrs = Object.entries(errors);

    for (const [key, value] of regErrs) {
      if (value) {
        if (key === 'global') {
          errorStep = 0; // global error, go to first step
          // stop the loop
          break;
        }
        errorStep = Math.min(config.stepsMap.inputs[key] ?? 1, errorStep);
      }
    }

    setState((prevState: RegisterModuleStateProps) => ({
      ...prevState,
      currentStep: errorStep ?? prevState.currentStep,
      fields: {
        ...prevState.fields,
        ...fields,
      },
      errors: {
        ...prevState.errors,
        global: errors.global ?? false,
      },
      loading: false,
      trigger: 'useEffect-errors',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  React.useEffect(() => {
    if (partialAccount?.error) {
      if (state.currentStep === config.stepsMap.otp || partialAccount?.errorCode === 10) {
        setState((prevState) => ({
          ...prevState,
          fields: {
            ...prevState.fields,
            otp: {
              ...prevState.fields.otp,
              error: partialAccount.errorMessage ?? 'REGISTER.INPUT.ERROR.OTP-INVALID',
            },
          },
          errors: {
            ...prevState.errors,
            otp: partialAccount.errorMessage ?? 'REGISTER.INPUT.ERROR.OTP-INVALID',
          },
          loading: false,
          currentStep: config.stepsMap.otp,
          trigger: 'REGISTER.INPUT.ERROR.OTP-INVALID',
        }));
      } else {
          setState((prevState) => ({
            ...prevState,
            currentStep: 0,
            errors: { ...prevState.errors, global: 'REGISTER.ERROR.UNKNOWN' },
            loading: false,
            trigger: 'unknown error',
          }));
      }
      // dispatch(setCanNavigateAction(true));
      //
      // navigate('/login');
    } else {
        setState((prevState) => ({
          ...prevState,
          // @ts-ignore
          currentStep: prevState.currentStep === config.stepsMap.otp && (OTPType === OTP_REGISTER_AUTO_LOGIN || loginError ||  state.fields.otp?.error) ? prevState.currentStep : prevState.savedStep, // TODO: refactor this!
          loading: false,
          trigger: 'partialAccount state change',
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partialAccount]);

  React.useEffect(() => {
    saveRegisterFieldsInStorage();
  }, [state.fields, state.checkboxes]);

  React.useEffect(() => {
    if (loginError) {

      const otpError = OTPType === 3 ? 'REGISTER.INPUT.ERROR.OTP-INVALID' : false;
      setState((v: RegisterModuleStateProps) => ({
        ...v,
        errors: {
          ...v.errors,
          otp: otpError,
          global: OTPType !== 3 ? 'GLOBAL_ERROR_LOGIN' : false, // TODO: redirect to login page?
        },
        fields: {
          ...v.fields,
          otp: {
            ...v.fields.otp,
            error: otpError,
          },
        },
        currentStep: otpError ? config.stepsMap.otp : v.currentStep,
        loading: false,
        resendTimer: 0, // to reactivate the resend button
        autoLogin: false
      }));
      // setOTPType(undefined);// reset OTP type
      return;
    }
  }, [loginError]);

  const saveRegisterFieldsInStorage = React.useCallback(() => {
    const fieldsToSave = ['email', 'phone', 'firstName', 'lastName', 'nin', 'address','username'];
    const checkboxesToSave = ['marketing_email', 'marketing_sms', 'marketing_phone', 'marketing_partner'];

    const fieldsData: { [key: string]: { value: string | boolean; type: 'field' | 'checkbox' } } = {};

    fieldsToSave.forEach((field) => {
      if (state.fields[field]) {
        fieldsData[field] = {
          //@ts-ignore
          val: state.fields[field]?.value,
          type: 'field',
        };
      }
    });
    checkboxesToSave.forEach((field) => {
      if (state.checkboxes[field]) {
        fieldsData[field] = {
          // @ts-ignore
          val: state.checkboxes[field]?.checked,
          type: 'checkbox',
        };
      }
    });

    const storageValues = loadRegisterFieldsFromStorage();

    if (storageValues) {
      if (JSON.stringify(fieldsData) !== JSON.stringify(storageValues)) {
        ExpiringLocalStorage.set('registerDynamicFields', JSON.stringify(fieldsData));
      }
    }
  }, [loadRegisterFieldsFromStorage, state.checkboxes, state.fields]);

  const onFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const type = event.target.type;
      const field = event.target.name;
      if (type === 'checkbox') {
        const value = event.target.checked;
        setState((prevState: RegisterModuleStateProps) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            global: false,
          },
          checkboxes: {
            ...prevState.checkboxes,
            [field]: {
              ...prevState.checkboxes[field],
              checked: value,
            },
          },
          trigger: `onFieldChange-${field}`,
        }));
      } else {
        let value = event.target.value;

        if (field === 'email') {
          // TODO: add hook validators with sanitization function
          value = value.toLowerCase();
        } else if (field === 'phone') {
          const regex = /^[0-9+ ]*$/;
          if (value && !regex.test(value)) {
            console.warn('Only digits are allowed', value);
            return;
          }

          if (value && value.includes('+') && value.lastIndexOf('+') !== 0) {
            console.warn('"+" is allowed only in the beggining', value);
            return;
          }
        }

        setState((prevState: RegisterModuleStateProps) => ({
          ...prevState,
          fields: {
            ...prevState.fields,
            [field]: {
              ...prevState.fields[field],
              value: value,
              error: undefined,
            },
          },
          errors: {
            ...prevState.errors,
            global: false,
          },
          trigger: `onFieldChange-${field}`,
        }));
        triggerValidator(field, value, field.includes('password') ? 0 : DEFAULT_TRIGGER_TIME);
      }
    },
    [setState, triggerValidator],
  );

  const onFieldBlur = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const field = event.target.name;
      const value = event.target.value;
      setState((prevState: RegisterModuleStateProps) => ({
        ...prevState,
        fields: {
          ...prevState.fields,
          [field]: {
            ...prevState.fields[field],
            focused: false,
            error: undefined,
          },
        },
        trigger: `onFieldBlur-${field}`,
      }));
      triggerValidator(field, value, 0);
    },
    [setState, triggerValidator],
  );

  const onFieldFocus = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const field = event.target.name;
      setState((prevState: RegisterModuleStateProps) => ({
        ...prevState,
        fields: {
          ...prevState.fields,
          [field]: {
            ...prevState.fields[field],
            error: undefined,
            focused: true,
          },
        },
        trigger: `onFieldFocus-${field}`,
      }));
    },
    [setState],
  );

  const getValues = React.useCallback(
    (forSignUp: boolean) => {
      const data: { [key: string]: string | number } = {};
      Object.keys(state.fields).forEach((key) => {
        data[key] = state.fields[key].value;
      });

      // default marketing is true, if not present in page
      let defaultMarketing = 1;
      // check if we have marketing field in checkboxes
      if (state.checkboxes.marketing) {
        defaultMarketing = state.checkboxes.marketing.checked ? 1 : 0;
      }
      if (state.checkboxes.marketing_email) {
        data.marketing_email = state.checkboxes.marketing_email.checked ? 1 : 0;
      } else {
        data.marketing_email = defaultMarketing;
      }
      if (state.checkboxes.marketing_phone) {
        data.marketing_phone = state.checkboxes.marketing_phone.checked ? 1 : 0;
      } else {
        data.marketing_phone = defaultMarketing;
      }
      if (state.checkboxes.marketing_sms) {
        data.marketing_sms = state.checkboxes.marketing_sms.checked ? 1 : 0;
      } else {
        data.marketing_sms = defaultMarketing;
      }
      if (state.checkboxes.marketing_partners) {
        data.marketing_partners = state.checkboxes.marketing_partners.checked ? 1 : 0;
      } else {
        data.marketing_partners = defaultMarketing;
      }

      data.marketingAgreement =
        data.marketing_email || data.marketing_phone || data.marketing_sms || data.marketing_partners;

      if (!forSignUp) {
        delete data.password;
      }
      delete data.confirmPassword;

      return data;
    },
    [state.checkboxes, state.fields],
  );

  const isNextButtonDisabled = React.useCallback(() => {
    if (config.stepsMap.tc === state.currentStep && !state.checkboxes.tc.checked) {
      return true;
    }

    let isDisabled = false;
    config.steps[state.currentStep].components.forEach((component) => {
      if (component.layout === 'input' || component.layout === 'password-input' || component.layout === 'phone-input') {
        // @ts-ignore
        if (!state.fields[component.type]) {
          isDisabled = true;
          // @ts-ignore
        } else if (state.fields[component.type]?.error) {
          isDisabled = true;
          // @ts-ignore
        } else if (state.fields[component.type]?.value === '' || state.fields[component.type]?.value === undefined) {
          isDisabled = true;
        }
      }
    });
    return isDisabled;
  }, [config.stepsMap.tc, config.steps, state.currentStep, state.checkboxes.tc.checked, state.fields]);

  /**
   * This function is called after login is done
   */
  React.useEffect(() => {
    if (!signedUp) {
      if (client_player_id) {
        setState((v) => ({
          ...v,
          currentStep: config.stepsMap.autoLoginStep,
        }));
      }
    }
  }, [client_player_id]);

  React.useEffect(() => {
    if (state.currentStep === config.stepsMap.otp && config.stepsMap.autoLoginStep > 0 && config.stepsMap.otp !== -1) {

      if (OTPType === OTP_REGISTER_AUTO_LOGIN) {
        dispatch(logout());
        dispatch(loginMethod(IS_PHONE));
        dispatch(loginUsername(state.fields.phone.value));
        dispatch(loginPassword(state.fields.otp.value));
        dispatch(loginStart(undefined));
        dispatch(setCanNavigateAction(true));
      } else {
        if (OTPType) {
          navigate('/login');
        }
      }
    }
  }, [state.autoLogin]);

  return useMemo(
    () => ({
      onFieldChange,
      onFieldBlur,
      onFieldFocus,
      triggerValidator,
      getValues,
      isNextButtonDisabled,
      getValuesFromLocalStorage,
      OTPType,
    }),
    [
      onFieldChange,
      onFieldBlur,
      onFieldFocus,
      triggerValidator,
      getValues,
      isNextButtonDisabled,
      getValuesFromLocalStorage,
      OTPType,
    ],
  );
}
